
import { useEffect, useMemo, useState } from 'react';
import {
  Infinity,
  LayoutGrid,
  Sparkles,
  Star,
  type LucideIcon,
} from 'lucide-react';
import {
  API_CATEGORY_DEFS,
  type PowerCategory,
  type PowerInfo,
} from '@/lib/powers-data';
import { getCategoryCountFor } from '@/lib/powers-cms';
import { useCatalogPowers } from '@/components/painel/powers/powers-cms-context';
import { smilieOrPawnImageCandidates } from '@/lib/xat-smilie';
import {
  POWER_CATEGORY_ACCENTS,
  POWER_CATEGORY_SMILEY_CODES,
  getPowerCategoryMutedTextColor,
  getPowerCategoryTileSurface,
  type PowerCategoryAccent,
} from '@/lib/power-category-theme';
import { cn } from '@/lib/utils';

type CategoryTile = {
  id: PowerCategory;
  label: string;
  count: number;
};

type TileTheme = {
  icon?: LucideIcon;
  smileyCode?: string;
  smileySizePx?: number;
  accent: PowerCategoryAccent;
  borderHover: string;
  shadowHover: string;
};

const DEFAULT_SMILEY_SIZE_PX = 40;
const GLINE_SMILEY_SIZE_PX = 30;
const LARGE_SMILEY_SIZE_PX = 44;
const PSTYLE_SMILEY_SIZE_PX = 52;
const COUPLE_SMILEY_SIZE_PX = 54;
const HEARTBREAK_SMILEY_SIZE_PX = 54;
const GAMER_SMILEY_SIZE_PX = 72;
const JINX_SMILEY_SIZE_PX = 54;
const SUPERSUMMER_SMILEY_SIZE_PX = 52;
/** Ícones de power no gs.xatblog.net só existem em 20/40/80/160 — fetch usa 40. */
const SMILEY_FETCH_SIZE_PX = 40;

const CATEGORY_SMILEY_SIZES: Record<string, number> = {
  smiley: GLINE_SMILEY_SIZE_PX,
  function: PSTYLE_SMILEY_SIZE_PX,
  stickers: COUPLE_SMILEY_SIZE_PX,
  group: LARGE_SMILEY_SIZE_PX,
  games: GAMER_SMILEY_SIZE_PX,
  hug: HEARTBREAK_SMILEY_SIZE_PX,
  collection: SUPERSUMMER_SMILEY_SIZE_PX,
  epic: DEFAULT_SMILEY_SIZE_PX,
  jinx: JINX_SMILEY_SIZE_PX,
};

const TILE_THEMES: Record<string, TileTheme> = {
  todos: {
    icon: LayoutGrid,
    accent: POWER_CATEGORY_ACCENTS.todos,
    borderHover: 'group-hover:border-indigo-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(99,102,241,0.14)]',
  },
  smiley: {
    smileyCode: POWER_CATEGORY_SMILEY_CODES.smiley,
    smileySizePx: CATEGORY_SMILEY_SIZES.smiley,
    icon: Sparkles,
    accent: POWER_CATEGORY_ACCENTS.smiley,
    borderHover: 'group-hover:border-yellow-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(250,204,21,0.14)]',
  },
  function: {
    smileyCode: POWER_CATEGORY_SMILEY_CODES.function,
    smileySizePx: CATEGORY_SMILEY_SIZES.function,
    icon: Sparkles,
    accent: POWER_CATEGORY_ACCENTS.function,
    borderHover: 'group-hover:border-blue-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(59,130,246,0.14)]',
  },
  stickers: {
    smileyCode: POWER_CATEGORY_SMILEY_CODES.stickers,
    smileySizePx: CATEGORY_SMILEY_SIZES.stickers,
    icon: Sparkles,
    accent: POWER_CATEGORY_ACCENTS.stickers,
    borderHover: 'group-hover:border-pink-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(236,72,153,0.14)]',
  },
  group: {
    smileyCode: POWER_CATEGORY_SMILEY_CODES.group,
    smileySizePx: CATEGORY_SMILEY_SIZES.group,
    icon: Sparkles,
    accent: POWER_CATEGORY_ACCENTS.group,
    borderHover: 'group-hover:border-cyan-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(6,182,212,0.14)]',
  },
  games: {
    smileyCode: POWER_CATEGORY_SMILEY_CODES.games,
    smileySizePx: CATEGORY_SMILEY_SIZES.games,
    icon: Sparkles,
    accent: POWER_CATEGORY_ACCENTS.games,
    borderHover: 'group-hover:border-orange-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(249,115,22,0.14)]',
  },
  hug: {
    smileyCode: POWER_CATEGORY_SMILEY_CODES.hug,
    smileySizePx: CATEGORY_SMILEY_SIZES.hug,
    icon: Sparkles,
    accent: POWER_CATEGORY_ACCENTS.hug,
    borderHover: 'group-hover:border-red-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(239,68,68,0.14)]',
  },
  collection: {
    smileyCode: POWER_CATEGORY_SMILEY_CODES.collection,
    smileySizePx: CATEGORY_SMILEY_SIZES.collection,
    icon: Sparkles,
    accent: POWER_CATEGORY_ACCENTS.collection,
    borderHover: 'group-hover:border-purple-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(168,85,247,0.14)]',
  },
  epic: {
    smileyCode: POWER_CATEGORY_SMILEY_CODES.epic,
    smileySizePx: CATEGORY_SMILEY_SIZES.epic,
    icon: Sparkles,
    accent: POWER_CATEGORY_ACCENTS.epic,
    borderHover: 'group-hover:border-amber-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(245,158,11,0.14)]',
  },
  jinx: {
    smileyCode: POWER_CATEGORY_SMILEY_CODES.jinx,
    smileySizePx: CATEGORY_SMILEY_SIZES.jinx,
    icon: Sparkles,
    accent: POWER_CATEGORY_ACCENTS.jinx,
    borderHover: 'group-hover:border-lime-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(132,204,22,0.14)]',
  },
  limited: {
    icon: Star,
    accent: POWER_CATEGORY_ACCENTS.limited,
    borderHover: 'group-hover:border-yellow-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(234,179,8,0.14)]',
  },
  unlimited: {
    icon: Infinity,
    accent: POWER_CATEGORY_ACCENTS.unlimited,
    borderHover: 'group-hover:border-emerald-400/40',
    shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(16,185,129,0.14)]',
  },
};

const FALLBACK_THEME: TileTheme = {
  icon: Sparkles,
  accent: POWER_CATEGORY_ACCENTS.todos,
  borderHover: 'group-hover:border-indigo-400/40',
  shadowHover: 'group-hover:shadow-[0_16px_40px_rgba(99,102,241,0.14)]',
};

const TILE_SIZE_MOBILE = 'h-[116px] w-[105px] shrink-0';
const TILE_SIZE_DESKTOP = 'lg:h-[116px] lg:w-full lg:min-w-0 lg:shrink';

const ALWAYS_VISIBLE_CATEGORIES = new Set<PowerCategory>(['function', 'stickers']);

function buildCategoryTiles(powers: PowerInfo[]): CategoryTile[] {
  const tiles: CategoryTile[] = [
    { id: 'todos', label: 'Todos', count: powers.length },
    ...API_CATEGORY_DEFS.map((cat) => ({
      id: cat.id as PowerCategory,
      label: cat.label,
      count: getCategoryCountFor(powers, cat.id),
    })).filter((c) => c.count > 0 || ALWAYS_VISIBLE_CATEGORIES.has(c.id)),
  ];

  const limitedCount = getCategoryCountFor(powers, 'limited');
  if (limitedCount > 0) {
    tiles.push({ id: 'limited', label: 'Limitados', count: limitedCount });
  }

  const unlimitedCount = getCategoryCountFor(powers, 'unlimited');
  if (unlimitedCount > 0) {
    tiles.push({ id: 'unlimited', label: 'Ilimitados', count: unlimitedCount });
  }

  return tiles;
}

interface PowersCategoriesProps {
  activeCategory: PowerCategory;
  onCategoryChange: (c: PowerCategory) => void;
}

function CategoryTileSmiley({
  code,
  sizePx = DEFAULT_SMILEY_SIZE_PX,
  fallbackIcon: FallbackIcon,
  iconColor,
}: {
  code: string;
  sizePx?: number;
  fallbackIcon?: LucideIcon;
  iconColor: string;
}) {
  const candidates = useMemo(
    () => smilieOrPawnImageCandidates(code, SMILEY_FETCH_SIZE_PX),
    [code]
  );
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [code]);

  const broken = candidateIndex >= candidates.length;
  const src = candidates[candidateIndex];

  if (broken) {
    if (!FallbackIcon) {
      return <span className="inline-block shrink-0" style={{ width: sizePx, height: sizePx }} aria-hidden />;
    }
    return <FallbackIcon className={cn('h-9 w-9', iconColor)} aria-hidden />;
  }

  return (
    <img
      src={src}
      alt=""
      width={sizePx}
      height={sizePx}
      className="max-h-none max-w-none object-contain"
      style={{ width: sizePx, height: sizePx }}
      aria-hidden
      loading="eager"
      decoding="async"
      draggable={false}
      onError={() => setCandidateIndex((index) => index + 1)}
    />
  );
}

function CategoryTileButton({
  tile,
  theme,
  isActive,
  onSelect,
}: {
  tile: CategoryTile;
  theme: TileTheme;
  isActive: boolean;
  onSelect: () => void;
}) {
  const Icon = theme.icon;
  const { accent } = theme;
  const surface = getPowerCategoryTileSurface(accent, isActive);

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onSelect}
      className={cn(
        'group relative overflow-hidden rounded-[17px]',
        TILE_SIZE_MOBILE,
        TILE_SIZE_DESKTOP,
        'border backdrop-blur-md',
        'flex flex-col items-center justify-center gap-2 px-2',
        'transition-all duration-300 ease-out',
        'hover:-translate-y-1.5',
        theme.borderHover,
        theme.shadowHover,
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030023]',
        isActive && 'ring-1 ring-white/10'
      )}
      style={surface}
    >
      <span
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 h-[52%] bg-gradient-to-t transition-opacity duration-300',
          isActive ? 'opacity-45' : 'opacity-[0.18] group-hover:opacity-35'
        )}
        style={{ backgroundImage: `linear-gradient(to top, ${accent.glow}, transparent)` }}
        aria-hidden
      />

      <span
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        aria-hidden
      >
        <span className="absolute -left-full top-0 h-full w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/[0.07] to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[280%]" />
      </span>

      <span className="relative flex h-11 w-11 shrink-0 items-center justify-center">
        {theme.smileyCode ? (
          <CategoryTileSmiley
            code={theme.smileyCode}
            sizePx={theme.smileySizePx ?? DEFAULT_SMILEY_SIZE_PX}
            fallbackIcon={Icon}
            iconColor={theme.accent.textClass}
          />
        ) : Icon ? (
          <Icon className={cn('h-9 w-9', theme.accent.textClass)} aria-hidden />
        ) : null}
      </span>

      <div className="relative min-w-0 w-full text-center">
        <p className="truncate font-poppins text-[11px] font-semibold leading-tight text-white sm:text-[12px]">
          {tile.label}
        </p>
        <p
          className="mt-1 font-poppins text-[10px] font-medium tabular-nums sm:text-[11px]"
          style={{ color: getPowerCategoryMutedTextColor(accent.hex) }}
        >
          {tile.count} powers
        </p>
      </div>
    </button>
  );
}

export default function PowersCategories({
  activeCategory,
  onCategoryChange,
}: PowersCategoriesProps) {
  const { powers: catalogPowers } = useCatalogPowers();
  const tiles = useMemo(() => buildCategoryTiles(catalogPowers), [catalogPowers]);

  function handleSelect(id: PowerCategory) {
    onCategoryChange(id);
  }

  return (
    <section className="relative border-t border-white/[0.06] bg-[#030023] py-12 md:py-14">
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#000b6a]/20 via-transparent to-transparent"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/20 to-transparent"
        aria-hidden
      />

      <div className="overflow-x-auto overflow-y-visible px-4 py-3 pb-1 [scrollbar-width:none] lg:overflow-x-visible lg:px-8 [&::-webkit-scrollbar]:hidden">
        <div
          className="flex w-max min-w-full gap-2.5 pe-4 lg:mx-auto lg:grid lg:w-[calc(100%-48px)] lg:pe-0"
          style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }}
          role="tablist"
          aria-label="Categorias de powers"
        >
          {tiles.map((tile) => (
            <div key={tile.id} className="flex h-[124px] shrink-0 items-end lg:min-w-0 lg:shrink">
              <CategoryTileButton
                tile={tile}
                theme={TILE_THEMES[tile.id] ?? FALLBACK_THEME}
                isActive={activeCategory === tile.id}
                onSelect={() => handleSelect(tile.id)}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
